import { Avatar, Box, Collection, CollectionItem, CollectionLink, Divider, Drawer, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { IOrganizationGetOne } from '../../interfaces/organizationGetOne';
import { OrganizationCareProviderDetails } from './OrganizationCareProviderDetails';

interface IOrganizationListDrawerProps {
    isOpen: boolean;
    onClose: (redirect?: boolean, url?: string) => void;
    onCloseComplete: () => void;
    organizationId: string | null;
}

export function OrganizationListDrawer(props: Readonly<IOrganizationListDrawerProps>) {
    const abortableFetch = useAbortableFetch();
    const [organization, setOrganization] = useState<IOrganizationGetOne>();
    const translate = getSelectedLanguage();

    assertHasAuthorization(ManagerAuthorization.organizationRead);

    useEffect(() => {
        if (props.organizationId) {
            loadOrganization(props.organizationId);
        }
    }, [props.organizationId]);

    if (!organization || !props.organizationId) {
        return null;
    }

    return (
        <Drawer
            isOpen={props.isOpen}
            onClose={() => props.onClose()}
            onCloseComplete={() => props.onCloseComplete()}
        >
            <Drawer.Header>{translate.organization.organization}</Drawer.Header>
            <Drawer.CloseButton />
            <Drawer.Body>
                <Collection
                    aria-label="organisatie lijst"
                    type="nav"
                >
                    {!organization.isKsyos || hasOneAuthorization(ManagerAuthorization.adminsRead) ? (
                        <CollectionLink
                            alignItems="start"
                            justifyContent="start"
                            mediaItem={
                                <Avatar
                                    icon="organization"
                                    shape="square"
                                    size={40}
                                />
                            }
                            onClick={() => {
                                props.onClose(true, `/organizations/${organization.id}/view`);
                            }}
                            padding="small"
                        >
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                            >
                                <Box mb={2}>
                                    <Text
                                        fontWeight="medium"
                                        size="md"
                                    >
                                        {organization.name}
                                    </Text>
                                </Box>
                                {(organization.street ||
                                    organization.houseNumber ||
                                    organization.houseNumberSuffix ||
                                    organization.zip ||
                                    organization.city) && (
                                    <Text size="sm">
                                        <Text
                                            as="span"
                                            fontWeight="medium"
                                            size="sm"
                                        >
                                            {translate.organization.address}:
                                        </Text>{' '}
                                        {organization.street} {organization.houseNumber}{' '}
                                        {organization.houseNumberSuffix}
                                        {(organization.street ||
                                            organization.houseNumber ||
                                            organization.houseNumberSuffix) &&
                                            (organization.zip || organization.city) &&
                                            ', '}
                                        {organization.zip} {organization.city}
                                    </Text>
                                )}
                                {organization.phoneNumber && (
                                    <Text size="sm">
                                        <Text
                                            as="span"
                                            fontWeight="medium"
                                            size="sm"
                                        >
                                            {translate.common.telephone}:
                                        </Text>{' '}
                                        {organization.phoneNumber}
                                    </Text>
                                )}
                                {organization.emailAddressRequestInbox && (
                                    <Text size="sm">
                                        <Text
                                            as="span"
                                            fontWeight="medium"
                                            size="sm"
                                        >
                                            {translate.common.email}:
                                        </Text>{' '}
                                        {organization.emailAddressRequestInbox}
                                    </Text>
                                )}
                            </Box>
                        </CollectionLink>
                    ) : (
                        <CollectionItem
                            alignItems="start"
                            justifyContent="start"
                            mediaItem={
                                <Avatar
                                    icon="organization"
                                    shape="square"
                                    size={40}
                                />
                            }
                            padding="small"
                            title={organization.name}
                        />
                    )}
                </Collection>
                <Divider color="minimal" />

                <OrganizationCareProviderDetails
                    avatarSize={40}
                    isKsyos={organization.isKsyos}
                    isPractitionerListInSidebar={true}
                    onClick={(url) => props.onClose(true, url)}
                    organizationId={props.organizationId}
                    padding="small"
                    shadow={false}
                />
            </Drawer.Body>
        </Drawer>
    );

    async function loadOrganization(organizationId: string) {
        const url = `/api/organizations/${organizationId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOrganization(result.json);
    }
}
